import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { dayParam, searchUpdate, text } from "@/lib/search";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { LuCalendarPlus, LuPlus, LuUserRound } from "react-icons/lu";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Field,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";
import { ListSkeleton } from "../components/LoadingSkeletons";
import { PageHeader } from "../components/PageHeader";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";
import { useViewerAccess } from "@/lib/access";
import { ActivityAdminAccessDenied, ActivityAdminLoadingState } from "./ActivityAdminShared";
import { participationRoleLabel, todayInVienna } from "./eventsShared";
import { addDaysLocal } from "./eventsShared";

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Events & Activities", to: "/activities" },
  { label: "Seminar planning" },
];

/**
 * Planning a term of a standing series.
 *
 * The empty slots are the screen. A series carries role slots for exactly one
 * reason -- so a gap can be shown (model §12) -- and a list of seminars that
 * are already arranged answers a question nobody is asking.
 */
export type SeminarPlanningSearch = {
  series?: string;
  /** Today when absent. */
  from?: string;
  /** 120 days on when absent. */
  to?: string;
};

export function validateSeminarPlanningSearch(search: Record<string, unknown>): SeminarPlanningSearch {
  return { series: text(search.series), from: dayParam(search.from), to: dayParam(search.to) };
}

export function SeminarPlanningPage() {
  const access = useViewerAccess();
  const isAdmin = access?.isActivityAdmin ?? false;

  const series = useQuery(api.events.listSeries, isAdmin ? {} : "skip");
  // The series and the range in the address, so a term being planned can be
  // sent to whoever helps fill it.
  const params = useSearch({ from: "/_authed/events/planning" });
  const navigateHere = useNavigate({ from: "/events/planning" });
  const setParams = (patch: SeminarPlanningSearch) =>
    void navigateHere({ search: (prev) => ({ ...prev, ...patch }), ...searchUpdate });
  const seriesId = params.series ?? "";
  const fromDay = params.from ?? todayInVienna();
  const toDay = params.to ?? addDaysLocal(todayInVienna(), 120);
  const [generating, setGenerating] = useState(false);
  const [filling, setFilling] = useState<{ eventId: Id<"events">; role: string; day: string } | null>(null);

  const chosen = seriesId === "" ? (series?.[0]?._id ?? "") : seriesId;

  const plan = useQuery(
    api.events.seminarPlan,
    chosen === "" ? "skip" : { seriesId: chosen as Id<"eventSeries">, fromDay, toDay },
  );

  const gaps = useMemo(
    () =>
      (plan?.occurrences ?? []).reduce(
        (sum, occurrence) =>
          sum + (occurrence.cancelled ? 0 : occurrence.slots.reduce((inner, slot) => inner + slot.open, 0)),
        0,
      ),
    [plan],
  );

  if (access === undefined) {
    return (
      <ActivityAdminLoadingState
        title="Seminar planning"
        description="The coming slots, and the empty ones."
        breadcrumbs={breadcrumbs}
      />
    );
  }

  if (!isAdmin) {
    return (
      <ActivityAdminAccessDenied
        title="Seminar planning"
        description="This page is for activity admins."
        breadcrumbs={breadcrumbs}
      />
    );
  }

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        breadcrumbs={breadcrumbs}
        eyebrow="Events & Activities"
        title="Seminar planning"
        description="Every occurrence in the range, who is in it, and where the gaps are."
        actions={
          <Button size="sm" colorPalette="blue" onClick={() => setGenerating(true)}>
            <LuCalendarPlus />Generate a term
          </Button>
        }
      />

      <Stack gap={5} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <HStack gap={3} wrap="wrap" align="end">
              <Field.Root maxW="16rem">
                <Field.Label>Series</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field value={chosen} onChange={(e) => setParams({ series: e.target.value })}>
                    {(series ?? []).map((row) => (
                      <option key={row._id} value={row._id}>{row.name}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root maxW="11rem">
                <Field.Label>From</Field.Label>
                <Input size="sm" type="date" value={fromDay} onChange={(e) => setParams({ from: e.target.value === "" ? undefined : e.target.value })} />
              </Field.Root>
              <Field.Root maxW="11rem">
                <Field.Label>To</Field.Label>
                <Input size="sm" type="date" value={toDay} onChange={(e) => setParams({ to: e.target.value === "" ? undefined : e.target.value })} />
              </Field.Root>
              <Box pb={1}>
                <Text fontSize="sm" color={gaps === 0 ? "fg.muted" : "orange.600"}>
                  {plan === undefined
                    ? ""
                    : gaps === 0
                      ? "Every slot in this range is filled."
                      : `${gaps} ${gaps === 1 ? "slot" : "slots"} still to fill.`}
                </Text>
              </Box>
        </HStack>

        {plan === undefined ? (
          <ListSkeleton label="Loading the term" rows={6} />
        ) : plan === null ? (
          <Card.Root borderWidth="1px" borderColor="border">
            <Card.Body p={6}><Text fontSize="sm" color="fg.muted">No series yet.</Text></Card.Body>
          </Card.Root>
        ) : plan.occurrences.length === 0 ? (
          <Card.Root borderWidth="1px" borderColor="border">
            <Card.Body p={6}>
              <Text fontSize="sm" color="fg.muted">
                No occurrences between those dates. Generate a term to lay them down.
              </Text>
            </Card.Body>
          </Card.Root>
        ) : (
          <Stack gap={3}>
            {plan.occurrences.map((occurrence) => (
              <Card.Root
                key={occurrence.eventId}
                borderWidth="1px"
                borderColor="border"
                opacity={occurrence.cancelled ? 0.55 : 1}
              >
                <Card.Body p={4}>
                  <HStack align="start" gap={5} wrap="wrap">
                    <Stack gap={0} minW="9rem">
                      <Link to="/events/$eventId" params={{ eventId: occurrence.eventId }}>
                        <Text fontWeight="semibold">{occurrence.startDay}</Text>
                      </Link>
                      <Text fontSize="xs" color="fg.muted">
                        {occurrence.startTime ?? ""}
                        {occurrence.location === "" ? "" : ` · ${occurrence.location}`}
                      </Text>
                      {occurrence.cancelled ? <Badge mt={1} colorPalette="red">Cancelled</Badge> : null}
                    </Stack>

                    <HStack gap={5} wrap="wrap" flex="1">
                      {occurrence.slots.map((slot) => (
                        <Stack key={slot.role} gap={1} minW="13rem">
                          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.16em" color="fg.muted">
                            {participationRoleLabel(slot.role)}
                          </Text>
                          {slot.people.map((person) => (
                            <HStack key={person.participationId} gap={2}>
                              <LuUserRound size={13} />
                              <Text fontSize="sm">{person.name}</Text>
                              {person.external ? (
                                <Badge variant="outline" colorPalette="purple">External</Badge>
                              ) : null}
                            </HStack>
                          ))}
                          {occurrence.cancelled
                            ? null
                            : Array.from({ length: slot.open }).map((_, index) => (
                              <Button
                                key={index}
                                size="xs"
                                variant="outline"
                                borderStyle="dashed"
                                justifyContent="start"
                                color="fg.muted"
                                onClick={() =>
                                  setFilling({
                                    eventId: occurrence.eventId,
                                    role: slot.role,
                                    day: occurrence.startDay,
                                  })}
                              >
                                <LuPlus />Empty
                              </Button>
                            ))}
                        </Stack>
                      ))}

                      {occurrence.others.length === 0 ? null : (
                        <Stack gap={1} minW="13rem">
                          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.16em" color="fg.muted">
                            Also
                          </Text>
                          {occurrence.others.map((person) => (
                            <Text key={person.participationId} fontSize="sm">
                              {person.name} — {participationRoleLabel(person.role)}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </HStack>
                  </HStack>
                </Card.Body>
              </Card.Root>
            ))}
          </Stack>
        )}
      </Stack>

      {chosen === "" ? null : (
        <>
          <GenerateTermDialog
            seriesId={chosen as Id<"eventSeries">}
            open={generating}
            onClose={() => setGenerating(false)}
          />
          <FillSlotDialog
            seriesId={chosen as Id<"eventSeries">}
            slot={filling}
            onClose={() => setFilling(null)}
          />
        </>
      )}
    </Box>
  );
}

/**
 * Who should take the slot.
 *
 * Ordered by how long it has been, never-done first, because that ordering IS
 * the question -- and it is answered by the server, so the page cannot sort it
 * subtly differently from the way the rota is meant to work.
 */
function FillSlotDialog({
  seriesId,
  slot,
  onClose,
}: {
  seriesId: Id<"eventSeries">;
  slot: { eventId: Id<"events">; role: string; day: string } | null;
  onClose: () => void;
}) {
  const candidates = useQuery(
    api.events.seminarCandidates,
    slot === null ? "skip" : { seriesId, role: slot.role as never },
  );
  const schedule = useMutation(api.participations.scheduleIntoSlot);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState<string | null>(null);

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const rows = candidates ?? [];
    return (needle === "" ? rows : rows.filter((row) => row.name.toLowerCase().includes(needle))).slice(0, 40);
  }, [candidates, filter]);

  async function pick(personId: Id<"persons">, name: string) {
    if (slot === null) {
      return;
    }
    setSaving(personId);
    try {
      await schedule({ eventId: slot.eventId, personId, role: slot.role as never, note: "" });
      toastSuccess(`${name} is down for ${slot.day}`, "They have been told.");
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, `${name} could not be scheduled`);
      toastError(message, detail);
    } finally {
      setSaving(null);
    }
  }

  return (
    <Dialog.Root open={slot !== null} onOpenChange={(e) => { if (!e.open) onClose(); }} size="lg">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>
                {slot === null ? "" : `${participationRoleLabel(slot.role)} on ${slot.day}`}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={3}>
                <Text fontSize="sm" color="fg.muted">
                  Scientific staff, longest since their last turn first. Counted over this series
                  only, so a conference talk never enters the rota.
                </Text>
                <Input
                  size="sm"
                  placeholder="Search by name…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />

                {candidates === undefined ? (
                  <ListSkeleton label="Loading" rows={3} />
                ) : shown.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">Nobody matches that.</Text>
                ) : (
                  <Stack gap={1} maxH="22rem" overflowY="auto">
                    {shown.map((candidate) => (
                      <HStack
                        key={candidate.personId}
                        justify="space-between"
                        gap={3}
                        px={3}
                        py={2}
                        borderWidth="1px"
                        borderColor="border"
                      >
                        <Stack gap={0} minW={0}>
                          <Text fontSize="sm" truncate>{candidate.name}</Text>
                          <Text fontSize="xs" color="fg.muted">
                            {candidate.lastDid === null
                              ? "Has never taken this slot"
                              : `Last on ${candidate.lastDid}`}
                          </Text>
                        </Stack>
                        <Button
                          size="xs"
                          variant="outline"
                          loading={saving === candidate.personId}
                          onClick={() => void pick(candidate.personId, candidate.name)}
                        >
                          Schedule
                        </Button>
                      </HStack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function GenerateTermDialog({
  seriesId,
  open,
  onClose,
}: {
  seriesId: Id<"eventSeries">;
  open: boolean;
  onClose: () => void;
}) {
  const generate = useMutation(api.events.generateOccurrences);
  const [draft, setDraft] = useState(() => ({
    weekday: "5",
    fromDay: todayInVienna(),
    toDay: addDaysLocal(todayInVienna(), 120),
    startTime: "12:00",
    endTime: "13:00",
    location: "",
  }));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const result = await generate({
        seriesId,
        weekday: Number(draft.weekday),
        fromDay: draft.fromDay,
        toDay: draft.toDay,
        startTime: draft.startTime === "" ? null : draft.startTime,
        endTime: draft.endTime === "" ? null : draft.endTime,
        location: draft.location,
        kind: "seminar",
      });
      toastSuccess(
        `${result.created} added`,
        result.skipped === 0
          ? undefined
          : `${result.skipped} already existed and were left alone.`,
      );
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The term could not be generated");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>Generate a term</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="fg.muted">
                  One occurrence a week between these dates. Days that already have one are left
                  alone, so extending a term is safe; holidays are cancelled by hand afterwards.
                </Text>
                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>Day of the week</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        value={draft.weekday}
                        onChange={(e) => setDraft({ ...draft, weekday: e.target.value })}
                      >
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                        <option value="0">Sunday</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Where</Field.Label>
                    <Input
                      size="sm"
                      value={draft.location}
                      onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                    />
                  </Field.Root>
                </HStack>
                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>From</Field.Label>
                    <Input
                      size="sm"
                      type="date"
                      value={draft.fromDay}
                      onChange={(e) => setDraft({ ...draft, fromDay: e.target.value })}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>To</Field.Label>
                    <Input
                      size="sm"
                      type="date"
                      value={draft.toDay}
                      onChange={(e) => setDraft({ ...draft, toDay: e.target.value })}
                    />
                  </Field.Root>
                </HStack>
                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>Starts</Field.Label>
                    <Input
                      size="sm"
                      type="time"
                      value={draft.startTime}
                      onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Ends</Field.Label>
                    <Input
                      size="sm"
                      type="time"
                      value={draft.endTime}
                      onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
                    />
                  </Field.Root>
                </HStack>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button colorPalette="blue" loading={saving} onClick={() => void save()}>Generate</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
